import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Popper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined'
import React from 'react'
import ReactDOM from 'react-dom'

import {
  APPROVAL_SOURCE_OPTIONS,
  addAptLine,
  aptRequestAction,
  deleteAllDaily,
  fetchAptList,
  fetchAptRequests,
  fetchMyAptRole,
  fetchAptCatalogue,
  fetchDailySheetTarget,
  pushDailyToSheet,
  restoreDaily,
  saveAptApproval,
  updateAptLine
} from 'api/apartmentProcurement'
import { fetchMyCentres, searchCatalogue } from 'api/procurement'
import DailyImportDialog from 'app/pages/Procurement/Hq/DailyImportDialog'
import PendingOrdersDialog from 'app/pages/Procurement/Hq/PendingOrdersDialog'
import TypedDeleteDialog from 'app/pages/Procurement/components/TypedDeleteDialog'
import {
  STATUS_META,
  apiErrorText,
  money
} from 'app/pages/Apartment/Procurement/constants'
import RequestTracking from 'app/pages/Apartment/Procurement/RequestTracking'
import { HeaderSlotContext } from 'app/pages/Apartment/Procurement/roleContext'
import { drawerWidth } from 'app/config'
import VendorManager from 'app/pages/Apartment/Procurement/VendorManager'
import {
  ADD_VENDOR,
  DISPATCH_OPTIONS,
  COL_VIEWS,
  DISPATCH_DOWNSTREAM,
  DISPATCH_LABEL,
  ITEM_TYPE_OPTIONS,
  NUM_FIELDS,
  PINNED_COLS,
  SHEET_COLS,
  categoryOptionsFor,
  sheetCategory,
  cellDead,
  invoiceEditable,
  matchesFilter,
  toSheetRows,
  vendorOptionsFrom
} from 'app/pages/Apartment/Procurement/sheetColumns'
import { useAptCategories } from 'app/pages/Apartment/Procurement/aptCategories'
import {
  NEW_CATEGORY,
  NewCategoryMenuItem,
  useNewCategoryPrompt
} from 'app/pages/Apartment/Procurement/NewCategory'
import {
  lineStage,
  lineStageText
} from 'app/pages/Apartment/Procurement/requestStage'
import useColumnBands, {
  bandHeaderRow,
  mapBandedCells
} from 'app/pages/Apartment/Procurement/useColumnBands'
import useSegmentGroups, {
  groupEntries
} from 'app/pages/Apartment/Procurement/useSegmentGroups'
import { apartmentPoSpecs } from 'app/pages/Procurement/components/apartmentPo'
import {
  downloadCsv,
  downloadOrderPdf,
  downloadOrderPdfSections
} from 'app/pages/Procurement/components/exportUtils'

// THE Daily Sheet — the register HQ maintains, same columns as the Google
// sheet (user 2026-07-17: keep the format intact, no separate At Apartment
// tab). Rows arrive two ways:
//   1. At Apartment requests: once the At Apartment head approves, they wait
//      in the review strip; HQ clicks ADD TO DAILY and the lines join the
//      register (then hand over -> LXD confirms receipt).
//   2. Direct entries: asks that came by Slack/mail/verbal — HQ types them
//      straight in via New line (Center = the centre/HQ, Apartment blank).
const HQ_EDITABLE = new Set([
  'vendor',
  'item_type',
  'ppp',
  'gst_rate',
  'amount_inclusive',
  'invoice_amount',
  'fulfilment_status',
  // HQ owns the sheet, so it can move any row to the day it belongs to.
  'sheet_date'
])

// HQ-only working columns the sheet kept in its later columns.
// Per-browser fold preference. A SEPARATE key from the requests grid: the
// two grids serve different jobs, so a person folding money away on one has
// not asked for it on the other.
const BANDS_KEY = 'hqDailySheet.foldedBands'
const GROUP_KEY = 'hqDailySheet.groupBySegment'
// Zero, same as the requests grid: only Product is pinned and the checkbox
// ahead of it scrolls away, so Product freezes flush against the sidebar.
const DAILY_LEAD_WIDTH = 0

/* ⭐⭐ Status and Dispatch are ONE column (Satya, 2026-09-02: "yes, make it in 1
 * column, like at apt"). They were two truths on one row: Status was the
 * REQUEST rolled up ("on daily sheet") while Dispatch beside it was THAT line
 * ("delivered / done"). A row of this sheet is a LINE and the dropdown writes
 * to the line, so the line is what the cell names — `lineStage` is the same
 * single source of stage vocabulary the At Apartment grid uses, so the two
 * registers cannot describe one PO differently.
 *
 * ⛔ The column is only dropped from the GRID. The Excel/PDF export keeps its
 * own Dispatch column — a spreadsheet has no chip to click, and HQ pivots on
 * that value.
 */
const DROPPED_COLS = ['fulfilment_status']
const GRID_COLS = SHEET_COLS.filter((c) => !DROPPED_COLS.includes(c.k))

const EXTRA_COLS = [
  { k: 'po_number', label: 'PO' },
  { k: 'expected_delivery', label: 'Expected' },
  { k: 'invoice_link', label: 'Invoice PDF' },
  { k: 'fulfilment_note', label: 'Remarks' }
]

const useStyles = makeStyles((theme) => ({
  bar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1)
  },
  // No overflow box (P3): the register scrolls with the PAGE so the freezes are
  // relative to the screen — header sticks to the top on scroll-down, the
  // leading identifier columns stick to the left on scroll-right.
  scroller: {},
  table: {
    minWidth: 2200,
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100]
    },
    '& tbody td': { boxShadow: 'inset 0 -1px 0 #eceae4' }
  },
  cell: { padding: '2px 6px', fontSize: 13, whiteSpace: 'nowrap' },
  // ---- collapsible column bands, shared with At Apartment -> Requests ----
  bandRow: {
    '& th': {
      top: 0,
      zIndex: 6,
      padding: '2px 6px',
      fontSize: 10,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      textAlign: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      lineHeight: 1.6
    }
  },
  bandRowSpacer: { cursor: 'default !important', background: 'transparent' },
  // The band header is the control that folds a run of columns away, so it has
  // to look like one. It was inheriting the plain grey header fill and read as
  // dead space (2026-08-26). OH orange, which nothing else in this grid uses.
  bandCell: {
    background: '#fdefea !important',
    color: '#f25e35',
    fontWeight: 700,
    borderBottom: '2px solid #f25e35 !important',
    '&:hover': { background: '#f9ddd2 !important' }
  },
  // Every body row carries a real background so the pinned cells can inherit
  // it. Without this the stripe is painted by the row and the frozen cells
  // inherit `transparent`, letting the scrolled columns show through them.
  tableRow: {
    background: '#fff',
    '&:nth-of-type(even)': { background: '#faf8f5' }
  },
  // The folded-band marker. ⛔ The tint lives on the HEADER only: painting it
  // down the body as well drew a full-height pink column through the table,
  // which reads as a broken empty column rather than a folded band (2026-08-26).
  // ⛔ A folded band must read as a SEAM, not a column. At 26px it was an empty
  // channel cutting through every row — and two adjacent folded bands made it
  // twice as wide (2026-08-26). 10px is enough to see and click, narrow enough
  // that the rows still read as continuous. The band name lives in the tooltip
  // and in the header row above, not in the cell.
  stubCell: {
    width: 10,
    minWidth: 10,
    maxWidth: 10,
    padding: '0 !important',
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: 9,
    lineHeight: 1,
    color: '#f25e35',
    background: '#fdefea'
  },
  // Body half: the row's own fill, so a fold is a gridline rather than a gap.
  stubBodyCell: {
    width: 10,
    minWidth: 10,
    maxWidth: 10,
    padding: '0 !important',
    background: 'inherit',
    borderRight: '1px solid #f0ddd4'
  },
  // The 10px stub carries a chevron, not the band name — the name is in the
  // tooltip and in the orange header row, which is where the label belongs.
  stubText: { fontSize: 11, fontWeight: 700, lineHeight: 1 },
  bandChip: {
    textTransform: 'none',
    fontSize: 12,
    padding: '2px 8px',
    minWidth: 0
  },
  groupRow: {
    cursor: 'pointer',
    '& td': {
      background: '#f7f4ee',
      fontWeight: 600,
      borderTop: '1px solid #e8e4dc'
    },
    '&:hover td': { background: '#f1ece2' }
  },
  groupMeta: { color: '#8a857c', fontWeight: 400, fontSize: 11, marginLeft: 7 },
  // ⛔ The page is the horizontal scroller (no overflow box — see `scroller`),
  // so `left` on a sticky cell is measured from the VIEWPORT, and the permanent
  // nav drawer covers the first `drawerWidth` px of it. Freezing at the bare
  // column offset parks the pinned columns UNDER the sidebar — that shipped on
  // 2026-08-26 and was only visible in a real browser, because jsdom has no
  // layout and no test could have caught it.
  // The offset rides in a custom property so the drawer width can be added in
  // CSS: below `sm` the drawer is a bottom sheet and takes no horizontal room.
  pinned: {
    position: 'sticky',
    zIndex: 4,
    // ⛔ NOT a hard white. A pinned cell has to keep whatever the ROW is
    // painting — the zebra stripe, the hover, the group tint — or the frozen
    // columns read as a white block sitting on top of striped rows, which is
    // exactly what shipped on 2026-08-26. `inherit` takes the tr's background,
    // and `tableRow` below guarantees the tr always has a concrete one so an
    // inherited transparent never lets the scrolled content show through.
    background: 'inherit',
    left: 'var(--pin-x)',
    [theme.breakpoints.up('sm')]: {
      left: `calc(var(--pin-x) + ${drawerWidth}px)`
    }
  },
  pinnedHead: { zIndex: 7 },
  pinnedEdge: { boxShadow: '2px 0 0 rgba(0,0,0,.12)' },
  filterHeadCell: { padding: '4px 6px' },
  filterInput: {
    backgroundColor: '#fff',
    '& input': { fontSize: 12, padding: '5px 6px' },
    '& input::placeholder': { color: theme.palette.text.secondary, opacity: 1 }
  },
  editInput: { '& input': { fontSize: 13, padding: '4px 2px' } },
  entryRow: { background: '#fffdf5' }
}))

// Live client-side preview of a line's Taxable / GST / Invoice / Total sides,
// mirroring the backend model.save(): print prices per side (sides x units),
// goods per unit (units x rate); GST-inclusive lines back-compute taxable+GST
// from the entered invoice. Used for the entry row so the totals fill in as you
// type (the server recomputes authoritatively on save).
const computeLine = (r) => {
  const qty = Number(r.qty) || 0
  const rate = Number(r.ppp) || 0
  const isPrint = (r.item_type || 'goods') === 'print'
  const sides = Number(r.sides_per_file) || 0
  const totalSides = isPrint ? sides * qty : 0
  const base = isPrint ? totalSides * rate : qty * rate
  const gstRate =
    r.gst_rate === '' || r.gst_rate == null ? 18 : Number(r.gst_rate) || 0
  let taxable = base
  let gst
  let invoice
  if (r.amount_inclusive) {
    invoice = Number(r.invoice_amount) || 0
    taxable = invoice / (1 + gstRate / 100)
    gst = invoice - taxable
  } else {
    gst = taxable * (gstRate / 100)
    invoice = taxable + gst
  }
  return { total_sides: totalSides, line_total: taxable, gst_amount: gst, invoice_amount: invoice }
}

// Today as the grid writes dates (YYYY-MM-DD), in the browser's own timezone —
// toISOString() alone is UTC and would hand back yesterday before 05:30 IST.
const todayStr = () => {
  const d = new Date()
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10)
}

// `day` is whatever date the sheet is showing, so a line typed while looking at
// a past day is filed against that day — that is how a missed day gets filled
// in. Empty (all days) means today.
const emptyEntry = (day) => ({
  sheet_date: day || todayStr(),
  // HQ's own working columns, typeable as the line is written (2026-07-23).
  fulfilment_status: 'pending',
  expected_delivery: '',
  invoice_link: '',
  fulfilment_note: '',
  at_apartment: false,
  apartment: '',
  centre_label: '',
  item_name: '',
  item_type: 'goods',
  category: '',
  vendor: '',
  qty: 1,
  print_specification: '',
  paper_kind: '',
  sides_per_file: '',
  ppp: '',
  gst_rate: 18,
  amount_inclusive: false,
  invoice_amount: '',
  file_link: '',
  reference_link: '',
  reference_id: '',
  source_of_request: ''
})

// A blank draft with a stable id, so selection and per-row updates survive rows
// being added, removed or filtered out (2026-07-23).
let _draftSeq = 0
const blankDraft = (day) => ({ ...emptyEntry(day), _id: (_draftSeq += 1) })

// True when a draft is still an untouched blank (only its date differs from a
// fresh one) — safe to clear away; a draft with anything typed is never dropped.
const draftUntouched = (d) => {
  const base = emptyEntry(d.sheet_date)
  return Object.keys(base).every(
    (k) => String(d[k] ?? '') === String(base[k] ?? '')
  )
}

// Product search for the entry row: type a name or reference id and pick a
// catalogue item to auto-fill the line (P9, 2026-07-21). At Apartment lines search
// the At Apartment catalogue; regular lines the main program-material one.
// Picking fills name / File-Link / category / print spec / sides (whatever the
// catalogue carries); every field stays editable afterwards.
function CatalogueField({ value, atApartment, onType, onPick, width }) {
  const [open, setOpen] = React.useState(false)
  const [results, setResults] = React.useState([])
  const aptCache = React.useRef(null)
  const anchorRef = React.useRef(null)

  const run = async (text) => {
    const q = String(text || '').trim().toLowerCase()
    try {
      if (atApartment) {
        if (!aptCache.current) {
          const res = await fetchAptCatalogue({ status: 'approved' })
          aptCache.current = Array.isArray(res) ? res : res?.results || []
        }
        setResults(
          aptCache.current
            .filter((it) => {
              const n = String(it.name || it.item_name || '').toLowerCase()
              const r = String(it.reference_id || '').toLowerCase()
              return !q || n.includes(q) || r.includes(q)
            })
            .slice(0, 15)
        )
      } else {
        const res = await searchCatalogue(text)
        setResults(res?.results || [])
      }
    } catch (e) {
      setResults([])
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <TextField
        inputRef={anchorRef}
        size="small"
        placeholder="Search product / ref id…"
        style={{ width: width || 170 }}
        InputProps={{ style: { fontSize: 13 } }}
        value={value || ''}
        onChange={(e) => {
          onType(e.target.value)
          setOpen(true)
          run(e.target.value)
        }}
        onFocus={() => {
          setOpen(true)
          run(value)
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      <Popper
        open={open && results.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1400 }}>
        <Paper elevation={4} style={{ maxHeight: 280, overflowY: 'auto', minWidth: 260 }}>
          {results.map((it) => (
            <MenuItem
              key={it.reference_id || it.uuid || it.name || it.item_name}
              onMouseDown={() => {
                onPick(it)
                setOpen(false)
              }}>
              <span style={{ fontSize: 13 }}>
                {it.name || it.item_name}
                {it.reference_id ? (
                  <span style={{ color: '#999' }}> · {it.reference_id}</span>
                ) : null}
                {it.category ? (
                  <span style={{ color: '#bbb' }}> · {it.category}</span>
                ) : null}
              </span>
            </MenuItem>
          ))}
        </Paper>
      </Popper>
    </div>
  )
}

const HqDailySheet = () => {
  const classes = useStyles()
  const headerSlot = React.useContext(HeaderSlotContext)
  // At Apartment categories added from Alfred (2026-09-17): re-render the
  // Category dropdowns when the list loads or grows. Above the early return.
  useAptCategories()
  const [askNewCategory, newCategoryDialog] = useNewCategoryPrompt()
  const [requests, setRequests] = React.useState(null)
  const [filters, setFilters] = React.useState({})
  // The new-line row is always on the sheet (2026-07-23): add as many lines as
  // you like, one after another, and remove a saved one with its delete button.
  // No open/close toggle.
  const entryOpen = true
  // The sheet keeps a LIST of blank draft rows, spreadsheet-style: "+ add line"
  // appends another, so clicking it five times gives five rows to fill (user
  // 2026-07-23). A draft persists to the server once it has a product and you
  // leave it or press Enter; then it drops out of the drafts and shows as a
  // real, per-cell-saving row.
  const [drafts, setDrafts] = React.useState(() => [blankDraft()])
  // Ticked draft rows (by _id) — the saved rows use `selected` (uuids); bulk
  // set / delete act on both (2026-07-23).
  const [selectedDrafts, setSelectedDrafts] = React.useState(() => new Set())
  // Ref lives with the other hooks, ABOVE the `if (!requests) return` early
  // exit — a hook after a conditional return changes the hook count between
  // renders and crashes the whole sheet (React #310, 2026-07-23).
  const entryRowRef = React.useRef(null)
  const [month, setMonth] = React.useState('') // '' = all months
  const [dateFrom, setDateFrom] = React.useState('')
  const [dateTo, setDateTo] = React.useState('')
  const [day, setDay] = React.useState('') // '' = no single-day filter (P6/P7)
  const [periodAnchor, setPeriodAnchor] = React.useState(null)
  const [vendorMgr, setVendorMgr] = React.useState(false)
  const [role, setRole] = React.useState({ is_hq: true, is_central: false })
  const [selected, setSelected] = React.useState(() => new Set())
  // Column-aligned bulk edit (2026-07-23): values keyed by the column's db
  // field. Fill any columns in the bulk row, Apply once → every ticked row gets
  // them. Replaces the old one-field-at-a-time dropdown.
  const [bulkValues, setBulkValues] = React.useState({})
  const [busy, setBusy] = React.useState(false)
  // What the API refused, shown under the toolbar. Without this a rejected save
  // looked like a dead button (2026-07-22).
  const [error, setError] = React.useState('')
  const [trackReq, setTrackReq] = React.useState(null)
  // Which row's stage menu is open. One at a time, keyed by the row so a
  // re-render cannot leave it anchored to a cell that has moved.
  const [stageAnchor, setStageAnchor] = React.useState(null)
  // The request whose approval sign-off is being edited in the popup, plus the
  // in-flight form values (approved_by_name / approval_source / reference).
  const [confirmDel, setConfirmDel] = React.useState(false)
  // The Google daily sheet is still where the monthly report is made, so HQ
  // pushes Alfred's lines back into it (2026-09-21). `sheetTarget` says
  // whether this environment has a workbook configured at all — without it the
  // action stays hidden rather than offering a button that can only fail.
  const [sheetTarget, setSheetTarget] = React.useState(null)
  const [pushOpen, setPushOpen] = React.useState(false)
  const [pushBusy, setPushBusy] = React.useState(false)
  const [pushDone, setPushDone] = React.useState(null)
  const [approvalReq, setApprovalReq] = React.useState(null)
  // Sign-off typed on the new-line row, stamped on the request the line lands
  // in once it is saved (2026-07-23).
  const EMPTY_APPROVAL = {
    approval_needed: '',
    approved_by_name: '',
    approval_source: '',
    approval_reference: ''
  }
  const [entryApproval, setEntryApproval] = React.useState(EMPTY_APPROVAL)
  const entryApprovalSet = Object.values(entryApproval).some(Boolean)
  const [approvalForm, setApprovalForm] = React.useState({})
  const [centres, setCentres] = React.useState([])
  const [apartments, setApartments] = React.useState([])
  const [importOpen, setImportOpen] = React.useState(false)
  const [fy, setFy] = React.useState('')
  const [lastDeleted, setLastDeleted] = React.useState(null)

  const fyOf = (row) => {
    const d = String(row.request.created || '').slice(0, 10)
    if (!d) return ''
    const y = Number(d.slice(0, 4))
    const m = Number(d.slice(5, 7))
    const start = m >= 4 ? y : y - 1
    return `FY ${start}-${String((start + 1) % 100).padStart(2, '0')}`
  }

  const deleteAll = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Clear all direct daily-sheet lines? You can Undo right after.')) return
    setBusy(true)
    try {
      const d = await deleteAllDaily()
      setLastDeleted(d.deleted || [])
      load()
    } finally {
      setBusy(false)
    }
  }
  const undoDelete = async () => {
    if (!lastDeleted || !lastDeleted.length) return
    setBusy(true)
    try {
      await restoreDaily(lastDeleted)
      setLastDeleted(null)
      load()
    } finally {
      setBusy(false)
    }
  }

  // Draft rows belong to the day they were created on (user 2026-07-23); the
  // render below shows only the day in view. Moving to a day clears away the
  // untouched blank rows left on other days (so they stop piling up) but keeps
  // every row that has anything typed, and ensures one blank row for the day now
  // in view.
  React.useEffect(() => {
    const d0 = day || todayStr()
    setDrafts((ds) => {
      const kept = ds.filter((d) => !draftUntouched(d) || d.sheet_date === d0)
      return kept.some((d) => draftUntouched(d) && d.sheet_date === d0)
        ? kept
        : [...kept, blankDraft(d0)]
    })
  }, [day])

  React.useEffect(() => {
    fetchMyCentres()
      .then((d) => setCentres(d.results || []))
      .catch(() => setCentres([]))
    fetchAptList()
      .then((list) => setApartments(Array.isArray(list) ? list : list.results || []))
      .catch(() => setApartments([]))
  }, [])

  const load = React.useCallback(() => {
    fetchAptRequests({
      status: 'final_approved,in_daily,handed_over,closed',
      lines: '1'
    }).then(setRequests)
  }, [])
  React.useEffect(() => {
    let alive = true
    fetchDailySheetTarget()
      .then((d) => alive && setSheetTarget(d))
      .catch(() => alive && setSheetTarget({ enabled: false }))
    return () => {
      alive = false
    }
  }, [])
  React.useEffect(load, [load])
  React.useEffect(() => {
    fetchMyAptRole()
      .then(setRole)
      .catch(() => setRole({ is_hq: true, is_central: false }))
  }, [])

  // ⛔ `add_to_daily` and `hand_over` are ONE-WAY. `reopen` refuses any request
  // HQ has already started on (it only accepts cluster_approved /
  // final_approved), so there is no in-app undo once either fires — an
  // accidental hand-over on 2026-08-26 had to be reversed with SQL.
  //
  // They used to sit on a MUI Chip's onClick AND its deleteIcon, which gave
  // three ways to fire and no confirmation — including Backspace/Delete, which
  // MUI routes to onDelete on keyup, so tabbing to a chip and hitting Backspace
  // handed over 46 lines. Same trap as the draft strip (PR #731); the fix is
  // the same: never put a real action in the delete slot.
  const [confirmAct, setConfirmAct] = React.useState(null)
  // Which list of waiting orders is open: 'review', 'hand_over' or none.
  const [pendingKind, setPendingKind] = React.useState(null)
  const askPending = (r) =>
    setConfirmAct(
      pendingKind === 'review'
        ? {
            uuid: r.uuid,
            action: 'add_to_daily',
            verb: 'Add to the daily sheet',
            apartment: r.apartment_name,
            lines: liveLines(r).length,
            note:
              'The lines join the register and a PO number is minted. ' +
              'The request can no longer be reopened for editing.'
          }
        : {
            uuid: r.uuid,
            action: 'hand_over',
            verb: 'Hand over',
            apartment: r.apartment_name,
            lines: liveLines(r).length,
            note:
              'This marks the goods as handed to the At Apartment team. ' +
              'It cannot be undone from Alfred.'
          }
    )
  const runConfirmed = async () => {
    const pending = confirmAct
    setConfirmAct(null)
    if (pending) await act(pending.uuid, pending.action)
  }

  // ---------------------------------------------------------- column bands
  // The same folding the At Apartment requests grid has: both render
  // SHEET_COLS, so a core capability belongs on both. Only the lead width
  // differs (this grid has one checkbox cell, that one has two leading cells).
  const {
    displayCols,
    pinFor,
    lastPinned,
    toggleBand,
    applyColView,
    activeColView
  } = useColumnBands(GRID_COLS, BANDS_KEY, PINNED_COLS, DAILY_LEAD_WIDTH)

  const {
    grouped,
    toggleGrouped,
    collapsed: collapsedGroups,
    toggleGroup,
    expandAll,
    collapseAllOf
  } = useSegmentGroups(GROUP_KEY)

  const pinProps = React.useCallback(
    (col, extraClass) => {
      const pin = pinFor(col)
      if (!pin) return { className: extraClass }
      return {
        className: [
          extraClass,
          classes.pinned,
          col.k === lastPinned ? classes.pinnedEdge : ''
        ]
          .filter(Boolean)
          .join(' '),
        style: {
          '--pin-x': `${pin.left}px`,
          width: pin.width,
          minWidth: pin.width,
          maxWidth: pin.width
        }
      }
    },
    [pinFor, lastPinned, classes]
  )

  const bandHeaderCells = React.useMemo(
    () => bandHeaderRow(displayCols, classes, toggleBand),
    [displayCols, classes, toggleBand]
  )

  const mapSheetCells = React.useCallback(
    (fn) => mapBandedCells(displayCols, classes, toggleBand, fn),
    [displayCols, classes, toggleBand]
  )

  if (!requests) return <LinearProgress />

  // Review strip: approved requests HQ has not yet added to the register.
  // Both strips skip requests with no lines left. Deleting the last line off
  // the sheet leaves the request behind — an empty one has nothing to review or
  // hand over, and a chip reading "· 0 lines" is just a dead button
  // (2026-07-22). The request itself is kept: adding a line to that apartment
  // finds it again and reuses its PO.
  // Lines HQ removed are tombstoned for the At Apartment team, not shown to HQ.
  const liveLines = (r) => (r.lines || []).filter((l) => !l.removed_by_hq)
  const hasLines = (r) => liveLines(r).length > 0
  const toReview = requests.filter(
    (r) => r.status === 'final_approved' && hasLines(r)
  )
  // Hand-over strip: apartment requests on the sheet, not yet handed to LXD.
  const toHandOver = requests.filter(
    (r) => r.status === 'in_daily' && r.apartment && hasLines(r)
  )

  const allRows = toSheetRows(
    requests.filter((r) => r.status !== 'final_approved')
  )
    // Removed lines are hidden — EXCEPT ones the At Apartment team flagged for
    // clarification, which resurface here so HQ can see and respond.
    .filter((row) => !row.removed_by_hq || row.clarification_requested)
    .map((row) => ({
      ...row,
      direct: !row.request.apartment,
      editable: row.status === 'in_daily' || row.status === 'handed_over'
    }))

  const monthOf = (row) => String(row.request.created || '').slice(0, 7)
  const monthLabel = (key) => {
    const [y, m] = key.split('-')
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${names[Number(m) - 1]} ${y}`
  }
  const months = [...new Set(allRows.map(monthOf).filter(Boolean))].sort().reverse()
  const fyList = [...new Set(allRows.map(fyOf).filter(Boolean))].sort().reverse()

  // The day a row belongs to. Lines carry their own date now; the ones written
  // before that fall back to when their request was opened, which is what the
  // sheet used to show for everything.
  const dateOf = (row) =>
    String(row.sheet_date || row.request.created || '').slice(0, 10)
  const rows = allRows.filter(
    (row) =>
      (!fy || fyOf(row) === fy) &&
      (!month || monthOf(row) === month) &&
      (!day || dateOf(row) === day) &&
      (!dateFrom || dateOf(row) >= dateFrom) &&
      (!dateTo || dateOf(row) <= dateTo) &&
      SHEET_COLS.every((c) => {
        if (!c.filter || !filters[c.k]) return true
        const value =
          c.k === 'status'
            ? // The filter has to match what the CELL shows, or typing a stage
              // that is on screen returns nothing — a rule this grid has been
              // bitten by before.
              lineStageText(row.request, row) || row.status
            : // ⭐⭐ Product searches the GAME as well as the item name (Satya,
            // 2026-09-02: "game wise i cannot search in the daily sheet"). A
            // game's lines are named after its PIECES — Tale trail is Story Mat
            // / Object cards / Character cards — so the game's own name is in no
            // cell and typing it returned an empty sheet. The At Apartment grid
            // learned this the same day; a search is core work, so it belongs on
            // both registers.
            // ⛔ Joined with ' · ', not a space: a space lets a query that
            // straddles the two fields ("cards tale") match by accident.
            c.k === 'item_name'
            ? [row.item_name, row.segment].filter(Boolean).join(' · ')
            : row[c.k]
        return matchesFilter(value, filters[c.k])
      })
  )

  const vendorOpts = vendorOptionsFrom(allRows)

  // Bulk selection for the delivery-date / dispatch action (P1, 2026-07-18):
  // with hundreds of lines, HQ sets the expected date (or dispatch state) on
  // many lines at once instead of one by one.
  const editableRows = rows.filter((r) => r.editable)
  // Draft rows currently on screen (the ones for the day in view).
  const visibleDrafts = drafts.filter((d) => !day || d.sheet_date === day)
  const selCount = selected.size + selectedDrafts.size
  const allSelectable = editableRows.length + visibleDrafts.length
  const allSelected =
    allSelectable > 0 &&
    editableRows.every((r) => selected.has(r.uuid)) &&
    visibleDrafts.every((d) => selectedDrafts.has(d._id))
  const someSelected = selCount > 0 && !allSelected

  const toggleSel = (uuid) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }
  const toggleDraftSel = (id) => {
    setSelectedDrafts((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  // Select-all covers both saved editable rows and the draft rows in view.
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set())
      setSelectedDrafts(new Set())
    } else {
      setSelected(new Set(editableRows.map((r) => r.uuid)))
      setSelectedDrafts(new Set(visibleDrafts.map((d) => d._id)))
    }
  }
  const clearSel = () => {
    setSelected(new Set())
    setSelectedDrafts(new Set())
  }

  const setBulkVal = (k, v) => setBulkValues((p) => ({ ...p, [k]: v }))
  const bulkHasValues = Object.values(bulkValues).some(
    (v) => v !== '' && v != null
  )

  // Apply the filled bulk-row columns to every ticked row at once — saved rows
  // through the API, draft rows updated in place. Type "color print" under
  // Print specification (and anything else), Apply, done (2026-07-23).
  const applyBulkRow = async () => {
    const filled = Object.entries(bulkValues).filter(
      ([, v]) => v !== '' && v != null
    )
    if (!filled.length || selCount === 0) return
    const patch = {}
    for (const [k, v] of filled) patch[k] = NUM_FIELDS.has(k) ? Number(v) : v
    if (patch.fulfilment_status) {
      const today = new Date().toISOString().slice(0, 10)
      if (patch.fulfilment_status === 'ordered') patch.ordered_on = today
      if (patch.fulfilment_status === 'dispatched') patch.dispatched_on = today
      if (patch.fulfilment_status === 'delivered') patch.delivered_on = today
    }
    // At Apartment (at_apartment) belongs to the row's structure, not a line field —
    // it applies to draft rows (Yes → Corporate centre) and is dropped from the
    // API patch, since a saved line's apartment is fixed by its request.
    const { at_apartment: atApartmentVal, ...apiPatch } = patch
    // Draft rows: a pure local update.
    if (selectedDrafts.size) {
      const draftPatch = { ...apiPatch }
      if (atApartmentVal === 'yes') {
        draftPatch.at_apartment = true
        draftPatch.centre_label = 'Corporate'
      } else if (atApartmentVal === 'no') {
        draftPatch.at_apartment = false
        draftPatch.apartment = ''
      }
      setDrafts((ds) =>
        ds.map((d) => (selectedDrafts.has(d._id) ? { ...d, ...draftPatch } : d))
      )
    }
    setBusy(true)
    try {
      // Nothing to send if the only thing filled was At Apartment (not a line
      // field) — the draft update above already handled it.
      if (Object.keys(apiPatch).length) {
        const ids = editableRows
          .filter((r) => selected.has(r.uuid))
          .map((r) => r.uuid)
        for (const uuid of ids) {
          // eslint-disable-next-line no-await-in-loop
          await updateAptLine({ uuid, ...apiPatch })
        }
      }
      clearSel()
      setBulkValues({})
      load()
    } finally {
      setBusy(false)
    }
  }

  // Filling the bulk Product from the catalogue drops the item name plus the
  // details it carries into the bulk row, so Apply pushes them all to the
  // ticked rows at once (2026-07-23).
  const pickBulkCatalogue = (it) => {
    setBulkValues((p) => ({
      ...p,
      item_name: it.name || it.item_name || p.item_name,
      ppp: it.ppp != null && it.ppp !== '' ? it.ppp : p.ppp,
      reference_link: it.reference_link || p.reference_link,
      reference_id: it.reference_id || p.reference_id,
      category: sheetCategory(it.category, false) || p.category,
      vendor: it.vendor || p.vendor,
      // ⭐⭐ segment AND the second artwork file ride along too (2026-09-02).
      // Every pick path copied vendor but not these: a hand-added line landed
      // blank-segment (so it grouped nowhere, quietly undoing the 276-row
      // backfill) and with only the front PDF. The backend's bulk payload has
      // carried segment since #737 — only these four FE paths were left behind.
      segment: it.segment || p.segment,
      file_link: it.file_link || p.file_link,
      print_specification: it.print_specification || p.print_specification,
      sides_per_file:
        it.sides_per_file != null && it.sides_per_file !== ''
          ? it.sides_per_file
          : p.sides_per_file
    }))
  }

  // One cell of the column-aligned bulk-edit row. Returns null for columns that
  // can't be bulk-set (Sl No., Status, computed totals, PO, Approval), so those
  // cells stay empty and everything lines up.
  const renderBulkCell = (c) => {
    const k = c.db || c.k
    const val = bulkValues[k] ?? ''
    if (c.k === 'item_name') {
      // Catalogue search, like the entry row — type / pick to autofill.
      return (
        <CatalogueField
          value={bulkValues.item_name || ''}
          atApartment={false}
          width={c.width}
          onType={(v) => setBulkVal('item_name', v)}
          onPick={pickBulkCatalogue}
        />
      )
    }
    if (c.kind === 'at_apartment') {
      // Bulk-set At Apartment yes / no (applies to the draft rows being
      // entered; a saved line's apartment is fixed by its request).
      return (
        <Select
          value={val}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) => setBulkVal(k, e.target.value)}>
          <MenuItem value="">—</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
          <MenuItem value="no">No</MenuItem>
        </Select>
      )
    }
    if (c.kind === 'vendor') return vendorSelect(val, (v) => setBulkVal(k, v))
    if (c.kind === 'itemtype') {
      return (
        <Select
          value={val}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) => setBulkVal(k, e.target.value)}>
          <MenuItem value="">—</MenuItem>
          {ITEM_TYPE_OPTIONS.map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
      )
    }
    if (c.kind === 'dispatch') {
      return (
        <Select
          value={val}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) => setBulkVal(k, e.target.value)}>
          <MenuItem value="">—</MenuItem>
          {DISPATCH_OPTIONS.map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
      )
    }
    if (c.kind === 'select') {
      return (
        <Select
          value={val}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) =>
            e.target.value === NEW_CATEGORY
              ? askNewCategory((name) => setBulkVal(k, name))
              : setBulkVal(k, e.target.value)
          }>
          <MenuItem value="">—</MenuItem>
          {(c.options || []).map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
          {c.addNew && <NewCategoryMenuItem value={NEW_CATEGORY} />}
        </Select>
      )
    }
    if (
      c.kind === 'text' ||
      c.kind === 'num' ||
      c.kind === 'date' ||
      c.kind === 'link' ||
      k === 'expected_delivery' ||
      k === 'invoice_link' ||
      k === 'fulfilment_note'
    ) {
      return (
        <TextField
          className={classes.editInput}
          // eslint-disable-next-line no-nested-ternary
          type={
            c.kind === 'num'
              ? 'number'
              : c.kind === 'date' || k === 'expected_delivery'
              ? 'date'
              : 'text'
          }
          placeholder={c.label}
          style={{ width: c.width || 110 }}
          value={val}
          onChange={(e) => setBulkVal(k, e.target.value)}
        />
      )
    }
    return null
  }

  // Remove one draft row via its own delete button (mirrors the saved rows'
  // per-row delete). Keeps a blank for the day so there's still a row to type
  // in (2026-07-23).
  const removeDraft = (draft) => {
    setSelectedDrafts((prev) => {
      const n = new Set(prev)
      n.delete(draft._id)
      return n
    })
    setDrafts((ds) => {
      const next = ds.filter((d) => d !== draft)
      const d0 = day || todayStr()
      return next.some((d) => draftUntouched(d) && d.sheet_date === d0)
        ? next
        : [...next, blankDraft(d0)]
    })
  }

  // Delete a single line off the sheet (HQ can remove any row).
  const deleteRow = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${row.item_name}" from the sheet?`)) return
    setBusy(true)
    try {
      await updateAptLine({ uuid: row.uuid, delete: true })
      load()
    } finally {
      setBusy(false)
    }
  }

  // Delete every ticked line (the safe replacement for the old Delete-all): the
  // user selects the rows, then confirms this one warning.
  const deleteSelected = async () => {
    const uuids = rows.filter((r) => selected.has(r.uuid)).map((r) => r.uuid)
    // Ticked draft rows are just dropped from the list — nothing to delete
    // server-side. Keep a blank for the day so there's still a row to type in.
    if (selectedDrafts.size) {
      setDrafts((ds) => {
        const next = ds.filter((d) => !selectedDrafts.has(d._id))
        const d0 = day || todayStr()
        return next.some((d) => draftUntouched(d) && d.sheet_date === d0)
          ? next
          : [...next, blankDraft(d0)]
      })
      setSelectedDrafts(new Set())
    }
    if (!uuids.length) {
      setConfirmDel(false)
      return
    }
    setConfirmDel(false)
    setBusy(true)
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const uuid of uuids) {
        // eslint-disable-next-line no-await-in-loop
        await updateAptLine({ uuid, delete: true })
      }
      setSelected(new Set())
      load()
    } finally {
      setBusy(false)
    }
  }

  // Take one item off an approved order before it is added to the sheet. The
  // server keeps an At Apartment line as "removed by HQ" rather than deleting
  // it, so that team sees it went and can send it again.
  const removePendingLine = async (line) => {
    setBusy(true)
    setError('')
    try {
      await updateAptLine({ uuid: line.uuid, delete: true })
      load()
    } catch (e) {
      setError(apiErrorText(e))
    } finally {
      setBusy(false)
    }
  }

  // Clone one line straight onto the sheet (same fields, new line).
  const cloneRow = async (row) => {
    setBusy(true)
    setError('')
    try {
      await addAptLine({
        item_name: row.item_name,
        item_type: row.item_type,
        category: row.category,
        vendor: row.vendor,
        qty: row.qty,
        ppp: row.ppp,
        gst_rate: row.gst_rate,
        amount_inclusive: row.amount_inclusive,
        invoice_amount: row.invoice_amount,
        print_specification: row.print_specification,
        paper_kind: row.paper_kind,
        sides_per_file: row.sides_per_file,
        file_link: row.file_link,
        reference_link: row.reference_link,
        reference_id: row.reference_id,
        source_of_request: row.source_of_request,
        centre_label: row.centre_label,
        apartment: (row.request && row.request.apartment) || '',
        direct: true
      })
      load()
    } catch (e) {
      setError(`Could not copy the line — ${apiErrorText(e)}`)
    } finally {
      setBusy(false)
    }
  }

  /* Setting a line's stage. ⭐⭐ It STAMPS THE DATE for the stage it moves to —
   * `ordered_on` / `dispatched_on` / `delivered_on` — which the At Apartment
   * grid's equivalent does not, because HQ's expected-vs-actual reporting is
   * built on those three columns. Merging the two columns must not quietly
   * drop that; this is the one setter both the merged chip's arrow and the
   * bulk row go through.
   * ⛔ Only stamps a date that is not already set — re-picking a stage must not
   * rewrite the day the goods actually moved.
   */
  const setDispatch = (row, status) => {
    const today = new Date().toISOString().slice(0, 10)
    const patch = { fulfilment_status: status }
    if (status === 'ordered' && !row.ordered_on) patch.ordered_on = today
    if (status === 'dispatched' && !row.dispatched_on) patch.dispatched_on = today
    if (status === 'delivered' && !row.delivered_on) patch.delivered_on = today
    return updateAptLine({ uuid: row.uuid, ...patch }).then(load)
  }

  const saveCell = async (line, field, raw) => {
    let value = raw
    if (NUM_FIELDS.has(field)) value = raw === '' ? '' : Number(raw)
    if (String(line[field] ?? '') === String(value ?? '')) return
    setError('')
    try {
      await updateAptLine({ uuid: line.uuid, [field]: value })
    } catch (e) {
      setError(`Could not save that cell — ${apiErrorText(e)}`)
    }
    load()
  }

  const act = async (uuid, action) => {
    setBusy(true)
    setError('')
    try {
      await aptRequestAction(uuid, action)
      load()
    } catch (e) {
      setError(apiErrorText(e))
    } finally {
      setBusy(false)
    }
  }


  // "+ add line": append another blank draft row and scroll to it. Clicking it
  // five times gives five rows (user 2026-07-23).
  const addLineFromTop = () => {
    setDrafts((ds) => [...ds, blankDraft(day)])
    window.setTimeout(() => {
      const row = entryRowRef.current
      if (!row) return
      // Scroll DOWN to the new row only — never sideways. scrollIntoView and a
      // plain focus() both drag the wide sheet left/right; scroll the page by
      // its Y alone (omitting left keeps the horizontal position) and focus
      // without letting the browser scroll to the input (user 2026-07-23).
      const y = row.getBoundingClientRect().top + window.scrollY - 160
      window.scrollTo({ top: y, behavior: 'smooth' })
      const input = row.querySelector('input:not([type="checkbox"])')
      if (input) input.focus({ preventScroll: true })
    }, 60)
  }

  // Enough typed on a draft to be worth saving as a line.
  const draftReady = (d) =>
    !!d.item_name.trim() && !(d.at_apartment && !d.apartment)

  // Persist one draft row. `silent` is the auto-commit path (leave / Enter): an
  // incomplete row just isn't saved, no nagging. On success the draft drops out
  // of the list and reappears as a real, per-cell-saving row; the list always
  // keeps at least one blank row to type into.
  // Takes the draft OBJECT, not an index — the index shifts as rows come and go
  // and, worse, a row filtered out by a date change unmounts, so anything keyed
  // to it would be lost (2026-07-23).
  const commitDraft = async (draft, opts = {}) => {
    if (!draft) return
    if (!draft.item_name.trim()) {
      if (!opts.silent) setError('Add a product before saving the line.')
      return
    }
    if (draft.at_apartment && !draft.apartment) {
      if (!opts.silent) setError('Pick the apartment for this At Apartment line.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const { at_apartment: atApartment, apartment, _id, ...rest } = draft
      await addAptLine({
        ...rest,
        apartment: atApartment ? apartment : '',
        centre_label: atApartment ? 'Corporate' : rest.centre_label,
        direct: true
      })
      setSelectedDrafts((prev) => {
        const n = new Set(prev)
        n.delete(draft._id)
        return n
      })
      setDrafts((ds) => {
        const next = ds.filter((d) => d !== draft)
        const d0 = day || todayStr()
        return next.some((d) => draftUntouched(d) && d.sheet_date === d0)
          ? next
          : [...next, blankDraft(d0)]
      })
      load()
    } catch (e) {
      setError(`Could not add the line — ${apiErrorText(e)}`)
    } finally {
      setBusy(false)
    }
  }

  const vendorSelect = (current, onPick) => (
    <Select
      value={current || ''}
      displayEmpty
      style={{ fontSize: 13 }}
      onChange={(e) => {
        const v = e.target.value
        if (v === ADD_VENDOR) {
          const name = window.prompt('New vendor name:')
          if (name && name.trim()) onPick(name.trim())
          return
        }
        onPick(v)
      }}>
      <MenuItem value="">—</MenuItem>
      {(current && !vendorOpts.includes(current)
        ? [current, ...vendorOpts]
        : vendorOpts
      ).map((o) => (
        <MenuItem key={o} value={o}>
          {o}
        </MenuItem>
      ))}
      <MenuItem value={ADD_VENDOR}>+ Add new vendor…</MenuItem>
    </Select>
  )

  // Fill a draft row from a picked catalogue item (P9). Only overwrite fields
  // the catalogue actually carries; everything stays editable afterwards.
  // `setEntry` is the target draft's own setter (drafts are a list now).
  const pickCatalogue = (it, setEntry) => {
    setEntry((p) => ({
      ...p,
      item_name: it.name || it.item_name || p.item_name,
      ppp: it.ppp != null && it.ppp !== '' ? it.ppp : p.ppp,
      reference_link: it.reference_link || p.reference_link,
      reference_id: it.reference_id || p.reference_id,
      category: sheetCategory(it.category, p.at_apartment) || p.category,
      vendor: it.vendor || p.vendor,
      // ⭐⭐ segment AND the second artwork file ride along too (2026-09-02).
      // Every pick path copied vendor but not these: a hand-added line landed
      // blank-segment (so it grouped nowhere, quietly undoing the 276-row
      // backfill) and with only the front PDF. The backend's bulk payload has
      // carried segment since #737 — only these four FE paths were left behind.
      segment: it.segment || p.segment,
      file_link: it.file_link || p.file_link,
      print_specification: it.print_specification || p.print_specification,
      sides_per_file:
        it.sides_per_file != null && it.sides_per_file !== ''
          ? it.sides_per_file
          : p.sides_per_file
    }))
  }

  // Open the approval-sign-off popup for a request, prefilled with its values.
  // `req` null = the new-line row: there is no request to stamp yet, so the
  // sign-off is held in `entryApproval` and applied once the line is saved.
  const openApproval = (req, anchorEl) => {
    setApprovalReq({ req, anchorEl, entry: !req })
    setApprovalForm(
      req
        ? {
            approval_needed: req.approval_needed || '',
            approved_by_name: req.approved_by_name || '',
            approval_source: req.approval_source || '',
            approval_reference: req.approval_reference || ''
          }
        : { ...entryApproval }
    )
  }

  // Bulk approval: fill the sign-off once, stamp it on every request behind the
  // ticked lines (each request stamped once even if several of its lines are
  // selected).
  const bulkReqIds = () => [
    ...new Set(
      rows
        .filter((r) => selected.has(r.uuid) && r.request)
        .map((r) => r.request.uuid)
    )
  ]
  const openBulkApproval = (anchorEl) => {
    setApprovalReq({ req: null, anchorEl, bulk: true, count: bulkReqIds().length })
    setApprovalForm({
      approval_needed: '',
      approved_by_name: '',
      approval_source: '',
      approval_reference: ''
    })
  }

  const submitApproval = async () => {
    if (!approvalReq) return
    if (approvalReq.entry) {
      // Nothing to save yet — hold it against the line being typed.
      setEntryApproval({ ...approvalForm })
      setApprovalReq(null)
      return
    }
    setBusy(true)
    try {
      if (approvalReq.bulk) {
        // eslint-disable-next-line no-restricted-syntax
        for (const uuid of bulkReqIds()) {
          // eslint-disable-next-line no-await-in-loop
          await saveAptApproval({ uuid, ...approvalForm })
        }
        setApprovalReq(null)
        setSelected(new Set())
        await load()
        return
      }
      await saveAptApproval({ uuid: approvalReq.req.uuid, ...approvalForm })
      setApprovalReq(null)
      await load()
    } finally {
      setBusy(false)
    }
  }

  const displayCell = (row, col) => {
    const v = row[col.k]
    if (col.kind === 'approval') {
      const r = row.request || {}
      const needsNo = r.approval_needed === 'no'
      const signed = [r.approved_by_name, r.approval_source].filter(Boolean).join(' · ')
      const label = needsNo
        ? 'No approval needed'
        : signed || (r.approval_needed === 'yes' ? 'Needs approval' : 'Set approval')
      const filled = needsNo || !!signed
      return (
        <Chip
          size="small"
          variant={filled ? 'default' : 'outlined'}
          label={label}
          onClick={(e) => openApproval(row.request, e.currentTarget)}
          title={
            r.approval_reference
              ? `Ref: ${r.approval_reference}`
              : 'Set whether approval is needed, and who approved / how'
          }
          style={{
            height: 20,
            fontSize: 11,
            cursor: 'pointer',
            maxWidth: 150,
            color: filled ? '#fff' : undefined,
            background: needsNo ? '#607d8b' : signed ? '#2e7d32' : undefined
          }}
        />
      )
    }
    if (col.k === 'status') {
      // ⭐⭐ The merged Status cell. The chip says where THIS LINE is; the arrow
      // beside it moves it on. Click the chip for the full dated tracking.
      const meta = lineStage(row.request, row)
      const current = row.fulfilment_status || 'pending'
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <Chip
            size="small"
            label={meta.label}
            onClick={() => setTrackReq(row.request)}
            title="Click for this line's full dated tracking"
            style={{
              background: meta.color,
              color: '#fff',
              height: 20,
              fontSize: 11,
              cursor: 'pointer'
            }}
          />
          <IconButton
            size="small"
            title="Move this line to another stage"
            style={{ padding: 0 }}
            onClick={(e) => setStageAnchor({ el: e.currentTarget, row })}
          >
            <ArrowDropDownIcon fontSize="small" />
          </IconButton>
          {stageAnchor && stageAnchor.row.uuid === row.uuid && (
            <Menu
              anchorEl={stageAnchor.el}
              open
              onClose={() => setStageAnchor(null)}
            >
              {DISPATCH_OPTIONS.map(([value, label]) => (
                <MenuItem
                  key={value}
                  selected={value === current}
                  onClick={() => {
                    setStageAnchor(null)
                    setDispatch(row, value)
                  }}
                >
                  {label}
                </MenuItem>
              ))}
              {/* ⛔ The later hops are shown for context and are NOT settable
                  here. Hand-over is one-way and only reversible by SQL — it
                  already has its own confirmed button above the sheet, and a
                  second path to it in a row menu is exactly how 46 lines were
                  handed over by accident on 2026-08-26. */}
              <MenuItem disabled divider style={{ fontSize: 11, opacity: 0.7 }}>
                set later in the flow
              </MenuItem>
              {DISPATCH_DOWNSTREAM.map((label) => (
                <MenuItem key={label} disabled style={{ fontSize: 12.5 }}>
                  {label}
                </MenuItem>
              ))}
            </Menu>
          )}
        </span>
      )
    }
    if (col.kind === 'money') return Number(v) ? money(v) : '—'
    if (col.kind === 'date') return dateOf(row) || '—'
    if (col.k === 'total_sides') return v ? Number(v) : '—'
    if (col.kind === 'link') {
      // A line's second file (file_link) has no column of its own — print jobs
      // read off a mail carry two PDFs for one centre, and sheet-imported lines
      // keep their File there — so it rides in this cell rather than vanishing.
      const second =
        col.k === 'reference_link' && row.file_link && row.file_link !== v
          ? row.file_link
          : ''
      if (!v && !second) return '—'
      return (
        <span>
          {v && (
            <Link href={v} target="_blank" rel="noopener noreferrer">
              {col.k === 'reference_link'
                ? 'file/link'
                : col.k === 'file_link'
                ? 'file'
                : 'link'}
            </Link>
          )}
          {v && second && ' · '}
          {second && (
            <Link
              href={second}
              target="_blank"
              rel="noopener noreferrer"
              title={second}
            >
              {v ? 'file 2' : 'file'}
            </Link>
          )}
        </span>
      )
    }
    if (col.k === 'fulfilment_status') {
      const fs = v || 'pending'
      return DISPATCH_LABEL[fs] || fs
    }
    if (col.k === 'apartment_name') return v || '—'
    if (col.kind === 'at_apartment') {
      const atApartment = !!(row.request && row.request.apartment) ||
        String(row.centre_label || '').toLowerCase() === 'corporate'
      return atApartment ? '✓' : '—'
    }
    if (col.kind === 'centre') {
      // Merged Center/Apartment column: show the apartment for At Apartment lines.
      const atApartment = !!(row.request && row.request.apartment)
      const val = atApartment ? row.apartment_name || row.centre_label : row.centre_label
      return val === '' || val == null ? '—' : String(val)
    }
    return v === '' || v == null ? '—' : String(v)
  }

  const editCell = (row, col) => {
    if (col.kind === 'vendor') {
      return vendorSelect(row.vendor, (v) => saveCell(row, 'vendor', v))
    }
    if (col.kind === 'itemtype') {
      return (
        <Select
          value={row.item_type || 'goods'}
          style={{ fontSize: 13 }}
          onChange={(e) => saveCell(row, 'item_type', e.target.value)}>
          {ITEM_TYPE_OPTIONS.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (col.kind === 'bool') {
      return (
        <Select
          value={row.amount_inclusive ? 'yes' : 'no'}
          style={{ fontSize: 13 }}
          onChange={(e) => saveCell(row, 'amount_inclusive', e.target.value === 'yes')}>
          <MenuItem value="no">No</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
        </Select>
      )
    }
    if (col.k === 'fulfilment_status') {
      return (
        <Select
          value={row.fulfilment_status || 'pending'}
          style={{ fontSize: 13 }}
          onChange={(e) => setDispatch(row, e.target.value)}>
          {DISPATCH_OPTIONS.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
          <MenuItem disabled divider style={{ fontSize: 11, opacity: 0.7 }}>
            set later in the flow
          </MenuItem>
          {DISPATCH_DOWNSTREAM.map((label) => (
            <MenuItem key={label} disabled style={{ fontSize: 12.5 }}>
              {label}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (col.kind === 'centre') {
      // At Apartment lines carry the apartment (fixed by the request), not a centre.
      if (row.request && row.request.apartment)
        return row.apartment_name || row.centre_label || '—'
      const opts = ['Corporate', ...centres.map((cc) => cc.name)]
      return (
        <Select
          value={row.centre_label || ''}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) => saveCell(row, 'centre_label', e.target.value)}>
          <MenuItem value="">—</MenuItem>
          {opts.map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
        </Select>
      )
    }
    if (col.k === 'category') {
      const atApartment =
        !!(row.request && row.request.apartment) ||
        String(row.centre_label || '').toLowerCase() === 'corporate'
      return (
        <Select
          value={row.category || ''}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) =>
            e.target.value === NEW_CATEGORY
              ? askNewCategory((name) => saveCell(row, 'category', name))
              : saveCell(row, 'category', e.target.value)
          }>
          <MenuItem value="">—</MenuItem>
          {categoryOptionsFor(atApartment).map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
          {atApartment && <NewCategoryMenuItem value={NEW_CATEGORY} />}
        </Select>
      )
    }
    if (col.kind === 'select') {
      return (
        <Select
          value={row[col.k] || ''}
          displayEmpty
          style={{ fontSize: 13 }}
          onChange={(e) => saveCell(row, col.k, e.target.value)}>
          <MenuItem value="">—</MenuItem>
          {col.options.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </Select>
      )
    }
    if (col.kind === 'date') {
      // Legacy rows have no date of their own — show the one the sheet has been
      // displaying, so moving a row to the right day is a single edit.
      return (
        <TextField
          key={`${row.uuid}-${col.k}-${row.modified || ''}`}
          className={classes.editInput}
          type="date"
          defaultValue={dateOf(row)}
          style={{ width: col.width || 130 }}
          onBlur={(e) => saveCell(row, col.k, e.target.value)}
        />
      )
    }
    return (
      <TextField
        key={`${row.uuid}-${col.k}-${row.modified || ''}`}
        className={classes.editInput}
        type={NUM_FIELDS.has(col.k) ? 'number' : 'text'}
        defaultValue={row[col.k] ?? ''}
        style={{ width: col.width || 80 }}
        onBlur={(e) => saveCell(row, col.k, e.target.value)}
      />
    )
  }

  // Apartment rows: HQ edits pricing/dispatch. Direct rows: HQ edits any
  // column (they never went through the requester flow) until closed.
  const cellFor = (row, col) => {
    if (!row.editable) return displayCell(row, col)
    // Print-only columns are dead on goods lines.
    if (cellDead(row, col)) return <span style={{ color: '#bbb' }}>—</span>
    const canEdit =
      (row.direct && col.db && row.status !== 'closed') || HQ_EDITABLE.has(col.k)
    if (!canEdit) return displayCell(row, col)
    // Invoice is editable only on GST-inclusive lines (it is the known total).
    if (col.k === 'invoice_amount') {
      if (!invoiceEditable(row)) return displayCell(row, col)
      return (
        <TextField
          key={`${row.uuid}-inv-${row.modified || ''}`}
          className={classes.editInput}
          type="number"
          defaultValue={row.invoice_amount ?? ''}
          style={{ width: 90 }}
          onBlur={(e) => saveCell(row, 'invoice_amount', e.target.value)}
        />
      )
    }
    return editCell(row, col)
  }

  // Plain-text value of a cell, for CSV/PDF export.
  const cellText = (row, col) => {
    if (col.k === 'status') {
      return lineStageText(row.request, row) || row.status || ''
    }
    if (col.kind === 'at_apartment') {
      const atApartment =
        !!(row.request && row.request.apartment) ||
        String(row.centre_label || '').toLowerCase() === 'corporate'
      return atApartment ? 'Yes' : 'No'
    }
    if (col.k === 'fulfilment_status') {
      const fs = row.fulfilment_status || 'pending'
      return DISPATCH_LABEL[fs] || fs
    }
    if (col.kind === 'approval') {
      const r = row.request || {}
      return [r.approved_by_name, r.approval_source, r.approval_reference]
        .filter(Boolean)
        .join(' · ')
    }
    if (col.kind === 'centre') {
      const atApartment = !!(row.request && row.request.apartment)
      return (atApartment ? row.apartment_name || row.centre_label : row.centre_label) || ''
    }
    if (col.kind === 'date') return dateOf(row)
    const v = row[col.k]
    return v == null ? '' : String(v)
  }

  // Register totals for the currently-shown rows.
  const totals = rows.reduce(
    (a, r) => ({
      taxable: a.taxable + Number(r.line_total || 0),
      gst: a.gst + Number(r.gst_amount || 0),
      invoice: a.invoice + Number(r.invoice_amount || 0)
    }),
    { taxable: 0, gst: 0, invoice: 0 }
  )

  const exportData = () => ({
    filename: `at-apartment-daily-sheet${month ? `-${month}` : ''}`,
    title: 'At Apartment — Daily Sheet',
    headers: [...SHEET_COLS.map((c) => c.label), ...EXTRA_COLS.map((c) => c.label)],
    rows: rows.map((row) => [
      ...SHEET_COLS.map((c) => cellText(row, c)),
      ...EXTRA_COLS.map((c) => (row[c.k] == null ? '' : String(row[c.k])))
    ])
  })

  const exportExcel = () => {
    const d = exportData()
    if (!d.rows.length) return
    downloadCsv(d.filename, d.headers, d.rows)
  }

  // What cut the view down, for the PO's "part of the full list" line.
  const filterNote = () => {
    const parts = []
    if (day) parts.push(`Day: ${day}`)
    if (month) parts.push(monthLabel(month))
    if (fy) parts.push(fy)
    if (dateFrom || dateTo) parts.push(`${dateFrom || '…'} – ${dateTo || '…'}`)
    SHEET_COLS.forEach((c) => {
      if (c.filter && filters[c.k]) parts.push(`${c.label}: ${filters[c.k]}`)
    })
    return parts.join(' · ')
  }

  // The PO export (user 2026-08-26): a PO DOCUMENT per request in view — never
  // the 25-column register dump. Ticked rows win when any are ticked, else
  // everything the filters left visible; a cut of a PO says "23 of 30 lines"
  // and what filtered it (apartmentPo.js, shared with the requests grid).
  const generatePo = () => {
    const source = selected.size
      ? rows.filter((r) => selected.has(r.uuid))
      : rows
    const specs = apartmentPoSpecs({
      rows: source,
      filterNote: selected.size
        ? `${selected.size} ticked line${selected.size === 1 ? '' : 's'}`
        : filterNote()
    })
    if (!specs.length) return
    if (specs.length === 1) downloadOrderPdf(specs[0].filename, specs[0])
    else downloadOrderPdfSections('at-apartment-purchase-orders', specs)
  }

  // Push back to the Google daily sheet (2026-09-21). Same selection rule as
  // the PO — ticked rows win, else everything in view — but a line that has
  // already been written is dropped here as well as server-side, so the count
  // on the dialog is the count that will land.
  const pushSource = () => {
    const source = selected.size
      ? rows.filter((r) => selected.has(r.uuid))
      : rows
    return source.filter((r) => !r.daily_sheet_pushed_at)
  }
  const sendToSheet = async () => {
    const lines = pushSource().map((r) => r.uuid)
    if (!lines.length) return
    setPushBusy(true)
    setError('')
    try {
      const res = await pushDailyToSheet(lines)
      setPushDone(res)
      setPushOpen(false)
      await load()
    } catch (e) {
      setError(`Could not write the daily sheet — ${apiErrorText(e)}`)
      setPushOpen(false)
    } finally {
      setPushBusy(false)
    }
  }

  // One-line summary of the active period filter for the Period button.
  const periodLabel = (() => {
    if (dateFrom || dateTo) return `${dateFrom || '…'} – ${dateTo || '…'}`
    if (month) return monthLabel(month)
    if (fy) return fy
    return 'All time'
  })()
  const periodActive = !!(dateFrom || dateTo || month || fy)
  const clearPeriod = () => {
    setFy('')
    setMonth('')
    setDateFrom('')
    setDateTo('')
  }

  // Single-day navigation (P6/P7): Today, ◀ / ▶ to step a day, and a picker.
  const shiftDay = (delta) => {
    const d = new Date(`${day || todayStr()}T00:00:00`)
    d.setDate(d.getDate() + delta)
    setDay(
      new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 10)
    )
  }

  return (
    <Box>
      {(() => {
        // Option-1 toolbar (user 2026-07-21): the primary + management actions
        // ride the tab row up top-right; the slim bar below carries only the
        // totals and the single Period button. (Per-header search already
        // covers filtering, so no separate search box.)
        const topActions = (
          <>
            <Button
              size="small"
              color="primary"
              variant="contained"
              disableElevation
              onClick={addLineFromTop}>
              + Add line
            </Button>
            <Button size="small" variant="outlined" onClick={() => setVendorMgr(true)}>
              Vendors
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setImportOpen(true)}>
              ⬇ Pull from Sheet
            </Button>
            <Button size="small" variant="outlined" onClick={exportExcel}>
              ⬇ Excel
            </Button>
            <Button
              size="small"
              variant="outlined"
              style={{ marginLeft: 8 }}
              title={
                selected.size
                  ? `A PO document for the ${selected.size} ticked line${
                      selected.size === 1 ? '' : 's'
                    }`
                  : 'A PO document per request for the lines in view — filter or tick rows first to cut it down'
              }
              onClick={generatePo}>
              ⬇ Generate PO
            </Button>
            {sheetTarget && sheetTarget.enabled && (
              <Button
                size="small"
                variant="outlined"
                title={
                  selected.size
                    ? `Write the ${selected.size} ticked line${
                        selected.size === 1 ? '' : 's'
                      } into the Google daily sheet`
                    : 'Write the lines in view into the Google daily sheet — the monthly report is made from it'
                }
                onClick={() => setPushOpen(true)}>
                ⬆ Push to Sheet
              </Button>
            )}
          </>
        )
        return headerSlot ? ReactDOM.createPortal(topActions, headerSlot) : null
      })()}
      <Box className={classes.bar}>
        {!headerSlot && (
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            onClick={addLineFromTop}>
            + Add line
          </Button>
        )}
        <Typography variant="caption" color="textSecondary">
          {rows.length} line{rows.length === 1 ? '' : 's'}
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
          Taxable {money(totals.taxable)} · GST {money(totals.gst)} · Invoice{' '}
          {money(totals.invoice)}
        </Typography>
        <Box style={{ flex: 1 }} />
        {/* Fixed label: "Today" always jumps to today, and is highlighted only
            when today is the day in view — so the highlight reads as state, not
            as the action (2026-07-23). A specific day is cleared back to all
            days with the ✕ chip that appears only while one is picked. */}
        <Button
          size="small"
          variant={day === todayStr() ? 'contained' : 'outlined'}
          color={day === todayStr() ? 'primary' : 'default'}
          disableElevation
          title="Jump to today"
          onClick={() => setDay(todayStr())}>
          Today
        </Button>
        <Button size="small" onClick={() => shiftDay(-1)} title="Previous day">
          ◀
        </Button>
        <TextField
          type="date"
          size="small"
          title="Go to a date"
          className={classes.editInput}
          style={{ width: 140 }}
          value={day}
          onChange={(e) => setDay(e.target.value)}
        />
        <Button size="small" onClick={() => shiftDay(1)} title="Next day">
          ▶
        </Button>
        {day ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setDay('')}
            title="Clear the date filter and list every day">
            Show all days
          </Button>
        ) : (
          <Typography variant="caption" color="textSecondary">
            showing all days
          </Typography>
        )}
        <Button
          size="small"
          variant={periodActive ? 'contained' : 'outlined'}
          color={periodActive ? 'primary' : 'default'}
          disableElevation
          onClick={(e) => setPeriodAnchor(e.currentTarget)}>
          📅 Period: {periodLabel}
        </Button>
        {lastDeleted && lastDeleted.length > 0 && (
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={undoDelete}>
            Undo delete ({lastDeleted.length})
          </Button>
        )}
      </Box>
      {error && (
        <Box
          style={{
            margin: '0 0 8px',
            padding: '8px 12px',
            border: '1px solid #E4CFC6',
            background: '#FBEDE8',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
          <Typography variant="body2" style={{ color: '#B84427', flex: 1 }}>
            {error}
          </Typography>
          <Button size="small" onClick={() => setError('')}>
            Dismiss
          </Button>
        </Box>
      )}
      <Popover
        open={!!periodAnchor}
        anchorEl={periodAnchor}
        onClose={() => setPeriodAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box style={{ padding: 16, width: 260, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Box>
            <Typography variant="caption" color="textSecondary">Financial year</Typography>
            <Select
              value={fy}
              displayEmpty
              fullWidth
              style={{ fontSize: 13 }}
              onChange={(e) => setFy(e.target.value)}>
              <MenuItem value="">All FYs</MenuItem>
              {fyList.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Month</Typography>
            <Select
              value={month}
              displayEmpty
              fullWidth
              style={{ fontSize: 13 }}
              onChange={(e) => setMonth(e.target.value)}>
              <MenuItem value="">Any month</MenuItem>
              {months.map((m) => (
                <MenuItem key={m} value={m}>{monthLabel(m)}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">Custom range</Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <TextField
                type="date"
                size="small"
                title="From date"
                className={classes.editInput}
                style={{ flex: 1 }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <Typography variant="caption">–</Typography>
              <TextField
                type="date"
                size="small"
                title="To date"
                className={classes.editInput}
                style={{ flex: 1 }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Box>
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button size="small" onClick={clearPeriod} disabled={!periodActive}>
              Clear
            </Button>
            <Button size="small" color="primary" onClick={() => setPeriodAnchor(null)}>
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
      {(toReview.length > 0 || toHandOver.length > 0) && (
        <Box className={classes.bar}>
          {/* One button per kind, not one per order: the orders are listed,
              with their items, in the pop-up it opens (2026-10-09). */}
          {toReview.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => setPendingKind('review')}>
              {`Review · ${toReview.length} order${
                toReview.length === 1 ? '' : 's'
              }`}
            </Button>
          )}
          {toHandOver.length > 0 && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => setPendingKind('hand_over')}>
              {`Hand over · ${toHandOver.length} order${
                toHandOver.length === 1 ? '' : 's'
              }`}
            </Button>
          )}
        </Box>
      )}

      {selCount > 0 && (
        <Box
          className={classes.bar}
          style={{ background: '#eef4ff', padding: 8, borderRadius: 4 }}>
          <Typography variant="body2">
            <b>{selCount}</b> selected
          </Typography>
          <Typography variant="caption" color="textSecondary">
            fill the highlighted row below, then Apply
          </Typography>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            disabled={busy || !bulkHasValues}
            onClick={applyBulkRow}>
            Apply to {selCount}
          </Button>
          <span style={{ borderLeft: '1px solid #c7d5ee', height: 20 }} />
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={(e) => openBulkApproval(e.currentTarget)}>
            Set approval…
          </Button>
          <Button
            size="small"
            style={{ color: '#c62828' }}
            disabled={busy}
            onClick={() =>
              selected.size ? setConfirmDel(true) : deleteSelected()
            }>
            Delete {selCount}
          </Button>
          <Button size="small" onClick={clearSel}>
            Clear
          </Button>
        </Box>
      )}

      {/* Column views + band folding — the same control the At Apartment
          requests grid carries, on the same shared column set. */}
      <Box className={classes.bar}>
        <Typography variant="caption" color="textSecondary">
          Columns
        </Typography>
        {COL_VIEWS.map((v) => (
          <Button
            key={v.id}
            size="small"
            className={classes.bandChip}
            variant={activeColView === v.id ? 'contained' : 'outlined'}
            color={activeColView === v.id ? 'primary' : 'default'}
            disableElevation
            onClick={() => applyColView(v)}>
            {v.label}
          </Button>
        ))}
        {activeColView === '' && (
          <Typography variant="caption" color="textSecondary">
            custom
          </Typography>
        )}
        <span style={{ width: 12 }} />
        <Typography variant="caption" color="textSecondary">
          Rows
        </Typography>
        <Button
          size="small"
          className={classes.bandChip}
          variant={grouped ? 'contained' : 'outlined'}
          color={grouped ? 'primary' : 'default'}
          disableElevation
          title="Group the lines by segment — the catalogue's own grouping"
          onClick={toggleGrouped}>
          Group by segment
        </Button>
        {grouped && (
          <>
            <Button size="small" className={classes.bandChip} onClick={expandAll}>
              Expand all
            </Button>
            <Button
              size="small"
              className={classes.bandChip}
              onClick={() => collapseAllOf(rows)}>
              Collapse all
            </Button>
          </>
        )}
      </Box>

      <Paper variant="outlined" className={classes.scroller}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow className={classes.bandRow}>
              <TableCell padding="checkbox" className={classes.bandRowSpacer} />
              {bandHeaderCells}
              {EXTRA_COLS.map((c) => (
                <TableCell key={c.k} className={classes.bandRowSpacer} />
              ))}
              <TableCell className={classes.bandRowSpacer} />
            </TableRow>
            <TableRow>
              <TableCell padding="checkbox" className={classes.filterHeadCell}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                />
              </TableCell>
              {mapSheetCells((c) => {
                const pin = pinProps(c, classes.filterHeadCell)
                return (
                <TableCell
                  key={c.k}
                  className={[pin.className, classes.pinnedHead]
                    .filter(Boolean)
                    .join(' ')}
                  style={pin.style}>
                  <Tooltip title={c.tip || ''} arrow>
                    {c.filter ? (
                      <TextField
                        variant="outlined"
                        size="small"
                        className={classes.filterInput}
                        placeholder={c.label}
                        style={{ width: Math.max(c.width || 0, 90) }}
                        value={filters[c.k] || ''}
                        onChange={(e) => {
                          const v = e.target.value
                          setFilters((p) => ({ ...p, [c.k]: v }))
                        }}
                      />
                    ) : (
                      <span>{c.label}</span>
                    )}
                  </Tooltip>
                </TableCell>
                )
              })}
              {EXTRA_COLS.map((c) => (
                <TableCell key={c.k} className={classes.filterHeadCell}>
                  {c.label}
                </TableCell>
              ))}
              <TableCell className={classes.filterHeadCell} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>

            {/* Column-aligned bulk-edit row: appears when rows are ticked. Fill
                a value under any column, Apply, and every ticked row gets it. */}
            {selCount > 0 && (
              <TableRow style={{ background: '#fff7e0' }}>
                <TableCell padding="checkbox" className={classes.cell}>
                  <Tooltip title="Bulk fill the ticked rows" arrow>
                    <span style={{ fontSize: 16 }}>✎</span>
                  </Tooltip>
                </TableCell>
                {mapSheetCells((c) => {
                  const pin = pinProps(c, classes.cell)
                  return (
                    <TableCell
                      key={c.k}
                      className={pin.className}
                      style={pin.style}>
                      {renderBulkCell(c)}
                    </TableCell>
                  )
                })}
                {EXTRA_COLS.map((c) => (
                  <TableCell key={c.k} className={classes.cell}>
                    {renderBulkCell(c)}
                  </TableCell>
                ))}
                <TableCell className={classes.cell} align="right">
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={busy || !bulkHasValues}
                    onClick={applyBulkRow}>
                    Apply
                  </Button>
                </TableCell>
              </TableRow>
            )}

            {groupEntries(rows, grouped, collapsedGroups).map((entry) =>
              entry.kind === 'group' ? (
                <TableRow
                  key={`grp-${entry.key}`}
                  className={classes.groupRow}
                  onClick={() => toggleGroup(entry.key)}>
                  <TableCell padding="checkbox" className={classes.cell} />
                  {mapSheetCells((c) => {
                    const pin = pinProps(c, classes.cell)
                    // Segment name in the Product column: it is the pinned one,
                    // so the heading stays put when the register is scrolled right.
                    const body =
                      c.k === 'sl_no' ? (
                        <span style={{ color: '#8a857c', fontSize: 11 }}>
                          {entry.open ? '▾' : '▸'}
                        </span>
                      ) : c.k === 'item_name' ? (
                        <>
                          {entry.name}
                          <span className={classes.groupMeta}>
                            {entry.count} item{entry.count === 1 ? '' : 's'}
                          </span>
                        </>
                      ) : null
                    return (
                      <TableCell
                        key={c.k}
                        className={pin.className}
                        style={pin.style}>
                        {body}
                      </TableCell>
                    )
                  })}
                  {EXTRA_COLS.map((c) => (
                    <TableCell key={c.k} className={classes.cell} />
                  ))}
                  <TableCell className={classes.cell} />
                </TableRow>
              ) : (
                ((row) => (
              <TableRow
                key={row.uuid}
                hover
                className={classes.tableRow}
                style={
                  row.clarification_requested
                    ? { background: '#fff3e0' }
                    : row.resent_by_at_apartment
                    ? { background: '#e8f0fe' }
                    : undefined
                }
                title={
                  row.clarification_requested
                    ? 'At Apartment asked HQ to clarify why this line was removed'
                    : row.resent_by_at_apartment
                    ? 'Re-sent by At Apartment — please procure this again'
                    : undefined
                }>
                <TableCell className={classes.cell} padding="checkbox">
                  {row.clarification_requested && (
                    <Tooltip title="At Apartment is asking why this was removed — respond, or re-add it" arrow>
                      <span style={{ color: '#ef6c00', fontSize: 11, fontWeight: 700 }}>
                        ⚠ clarify
                      </span>
                    </Tooltip>
                  )}
                  {row.resent_by_at_apartment && !row.clarification_requested && (
                    <Tooltip title="Re-sent by At Apartment — procure again" arrow>
                      <span style={{ color: '#1565c0', fontSize: 11, fontWeight: 700 }}>
                        ↻ re-sent
                      </span>
                    </Tooltip>
                  )}
                  {row.editable && !row.clarification_requested && (
                    <Checkbox
                      size="small"
                      checked={selected.has(row.uuid)}
                      onChange={() => toggleSel(row.uuid)}
                    />
                  )}
                </TableCell>
                {mapSheetCells((col) => {
                  const pin = pinProps(col, classes.cell)
                  return (
                    <TableCell
                      key={col.k}
                      className={pin.className}
                      style={pin.style}>
                      {cellFor(row, col)}
                    </TableCell>
                  )
                })}
                <TableCell className={classes.cell}>{row.po_number || '—'}</TableCell>
                <TableCell className={classes.cell}>
                  {row.editable ? (
                    <TextField
                      type="date"
                      defaultValue={row.expected_delivery || ''}
                      className={classes.editInput}
                      style={{ width: 130 }}
                      onBlur={(e) =>
                        saveCell(row, 'expected_delivery', e.target.value || null)
                      }
                    />
                  ) : (
                    row.expected_delivery || '—'
                  )}
                </TableCell>
                <TableCell className={classes.cell}>
                  {row.editable ? (
                    <TextField
                      placeholder="Invoice PDF link"
                      defaultValue={row.invoice_link}
                      className={classes.editInput}
                      style={{ width: 130 }}
                      onBlur={(e) => saveCell(row, 'invoice_link', e.target.value)}
                    />
                  ) : row.invoice_link ? (
                    <Link
                      href={row.invoice_link}
                      target="_blank"
                      rel="noopener noreferrer">
                      invoice
                    </Link>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell className={classes.cell}>
                  {row.editable ? (
                    <TextField
                      placeholder="Remarks"
                      defaultValue={row.fulfilment_note}
                      className={classes.editInput}
                      style={{ width: 130 }}
                      onBlur={(e) =>
                        saveCell(row, 'fulfilment_note', e.target.value)
                      }
                    />
                  ) : (
                    row.fulfilment_note || '—'
                  )}
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  <Box display="flex" alignItems="center" justifyContent="flex-end">
                    <IconButton
                      size="small"
                      title="Clone this line onto the sheet"
                      disabled={busy}
                      onClick={() => cloneRow(row)}>
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Delete this line"
                      disabled={busy}
                      onClick={() => deleteRow(row)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
                ))(entry.row)
              )
            )}
            {!rows.length && (
              <TableRow>
                <TableCell
                  colSpan={displayCols.length + EXTRA_COLS.length + 2}
                  className={classes.cell}>
                  <Typography variant="body2" color="textSecondary">
                    Nothing on the daily sheet yet — approved requests appear in
                    the review strip above, or add a direct line.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {entryOpen &&
              // Only the draft rows for the day in view (or all of them when no
              // single day is selected).
              visibleDrafts.map((entry, vi, vis) => {
                // Per-draft setter keyed by the stable _id, so it hits the right
                // row even as rows are added / removed / filtered out.
                const setEntry = (updater) =>
                  setDrafts((ds) =>
                    ds.map((d) =>
                      d._id === entry._id
                        ? typeof updater === 'function'
                          ? updater(d)
                          : updater
                        : d
                    )
                  )
                return (
                  <TableRow
                    key={entry._id}
                    ref={vi === vis.length - 1 ? entryRowRef : undefined}
                    className={classes.entryRow}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        commitDraft(entry)
                      }
                    }}
                    onBlur={(e) => {
                      // Leaving a draft saves it if it's ready. Only skip while
                      // still editing a cell in this row, or a Select menu is
                      // open (its listbox steals focus through a portal). Do NOT
                      // skip just because the row is gone from the DOM — changing
                      // the date filters this row out, and its data must still
                      // be saved, not dropped (2026-07-23).
                      const row = e.currentTarget
                      window.setTimeout(() => {
                        if (row.isConnected && row.contains(document.activeElement)) return
                        if (document.querySelector('.MuiPopover-root, .MuiMenu-root'))
                          return
                        if (!busy && draftReady(entry)) commitDraft(entry, { silent: true })
                      }, 120)
                    }}>
                    <TableCell padding="checkbox" className={classes.cell}>
                      <Checkbox
                        size="small"
                        checked={selectedDrafts.has(entry._id)}
                        onChange={() => toggleDraftSel(entry._id)}
                      />
                    </TableCell>
                {mapSheetCells((c) => {
                  const pin = pinProps(c, classes.cell)
                  return (
                  <TableCell
                    key={c.k}
                    className={pin.className}
                    style={pin.style}>
                    {c.k === 'requested_by_name' ? (
                      <Typography variant="caption">direct entry</Typography>
                    ) : c.kind === 'at_apartment' ? (
                      <Checkbox
                        size="small"
                        checked={!!entry.at_apartment}
                        onChange={(e) => {
                          const on = e.target.checked
                          setEntry((p) => ({
                            ...p,
                            at_apartment: on,
                            centre_label: on ? 'Corporate' : '',
                            apartment: '',
                            category: ''
                          }))
                        }}
                      />
                    ) : c.kind === 'centre' ? (
                      entry.at_apartment ? (
                        <Select
                          value={entry.apartment || ''}
                          displayEmpty
                          style={{ fontSize: 13 }}
                          onChange={(e) =>
                            setEntry((p) => ({ ...p, apartment: e.target.value }))
                          }>
                          <MenuItem value="">Apartment…</MenuItem>
                          {apartments.map((a) => (
                            <MenuItem key={a.uuid} value={a.uuid}>{a.name}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Select
                          value={entry.centre_label || ''}
                          displayEmpty
                          style={{ fontSize: 13 }}
                          onChange={(e) =>
                            setEntry((p) => ({ ...p, centre_label: e.target.value }))
                          }>
                          <MenuItem value="">Centre…</MenuItem>
                          {centres.map((cc) => (
                            <MenuItem key={cc.uuid} value={cc.name}>{cc.name}</MenuItem>
                          ))}
                        </Select>
                      )
                    ) : c.k === 'item_name' ? (
                      <CatalogueField
                        value={entry.item_name}
                        atApartment={!!entry.at_apartment}
                        width={c.width}
                        onType={(v) =>
                          setEntry((p) => ({ ...p, item_name: v }))
                        }
                        onPick={(it) => pickCatalogue(it, setEntry)}
                      />
                    ) : c.k === 'category' ? (
                      <Select
                        value={entry.category || ''}
                        displayEmpty
                        style={{ fontSize: 13 }}
                        onChange={(e) =>
                          e.target.value === NEW_CATEGORY
                            ? askNewCategory((name) =>
                                setEntry((p) => ({ ...p, category: name }))
                              )
                            : setEntry((p) => ({ ...p, category: e.target.value }))
                        }>
                        <MenuItem value="">—</MenuItem>
                        {categoryOptionsFor(entry.at_apartment).map((o) => (
                          <MenuItem key={o} value={o}>{o}</MenuItem>
                        ))}
                        {entry.at_apartment && (
                          <NewCategoryMenuItem value={NEW_CATEGORY} />
                        )}
                      </Select>
                    ) : c.k === 'status' ? (
                      <Typography variant="caption">daily</Typography>
                    ) : cellDead({ item_type: entry.item_type }, c) ? (
                      <span style={{ color: '#bbb' }}>—</span>
                    ) : c.kind === 'vendor' ? (
                      vendorSelect(entry.vendor, (v) =>
                        setEntry((p) => ({ ...p, vendor: v }))
                      )
                    ) : c.kind === 'itemtype' ? (
                      <Select
                        value={entry.item_type || 'goods'}
                        style={{ fontSize: 13 }}
                        onChange={(e) => {
                          const v = e.target.value
                          setEntry((p) => ({ ...p, item_type: v }))
                        }}>
                        {ITEM_TYPE_OPTIONS.map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : c.kind === 'dispatch' ? (
                      // Settable as you type — HQ usually knows it already.
                      <Select
                        value={entry.fulfilment_status || 'pending'}
                        style={{ fontSize: 13 }}
                        onChange={(e) => {
                          const v = e.target.value
                          setEntry((p) => ({ ...p, fulfilment_status: v }))
                        }}>
                        {DISPATCH_OPTIONS.map(([value, lbl]) => (
                          <MenuItem key={value} value={value}>
                            {lbl}
                          </MenuItem>
                        ))}
                        <MenuItem disabled divider style={{ fontSize: 11, opacity: 0.7 }}>
                          set later in the flow
                        </MenuItem>
                        {DISPATCH_DOWNSTREAM.map((lbl) => (
                          <MenuItem key={lbl} disabled style={{ fontSize: 12.5 }}>
                            {lbl}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : c.kind === 'approval' ? (
                      // Approval is set on the row once it's saved (the sign-off
                      // belongs to the request, which doesn't exist yet).
                      <Typography variant="caption" style={{ color: '#bbb' }}>
                        after save
                      </Typography>
                    ) : c.kind === 'bool' ? (
                      <Select
                        value={entry.amount_inclusive ? 'yes' : 'no'}
                        style={{ fontSize: 13 }}
                        onChange={(e) => {
                          const yes = e.target.value === 'yes'
                          setEntry((p) => ({ ...p, amount_inclusive: yes }))
                        }}>
                        <MenuItem value="no">No</MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                      </Select>
                    ) : c.k === 'invoice_amount' ? (
                      entry.amount_inclusive ? (
                        <TextField
                          className={classes.editInput}
                          type="number"
                          placeholder="Invoice"
                          style={{ width: 90 }}
                          value={entry.invoice_amount ?? ''}
                          onChange={(e) => {
                            const v = e.target.value
                            setEntry((p) => ({ ...p, invoice_amount: v }))
                          }}
                        />
                      ) : (
                        <span style={{ color: '#555' }}>
                          {money(computeLine(entry).invoice_amount)}
                        </span>
                      )
                    ) : c.k === 'line_total' ? (
                      <span style={{ color: '#555' }}>
                        {money(computeLine(entry).line_total)}
                      </span>
                    ) : c.k === 'total_sides' ? (
                      <span style={{ color: '#555' }}>
                        {(entry.item_type || 'goods') === 'print'
                          ? computeLine(entry).total_sides || '—'
                          : '—'}
                      </span>
                    ) : c.kind === 'select' ? (
                      <Select
                        value={entry[c.k] || ''}
                        displayEmpty
                        style={{ fontSize: 13 }}
                        onChange={(e) => {
                          const v = e.target.value
                          setEntry((p) => ({ ...p, [c.k]: v }))
                        }}>
                        <MenuItem value="">—</MenuItem>
                        {c.options.map((o) => (
                          <MenuItem key={o} value={o}>
                            {o}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : c.kind === 'text' ||
                      c.kind === 'num' ||
                      c.kind === 'date' ||
                      c.kind === 'link' ? (
                      <TextField
                        className={classes.editInput}
                        type={
                          // eslint-disable-next-line no-nested-ternary
                          c.kind === 'num'
                            ? 'number'
                            : c.kind === 'date'
                            ? 'date'
                            : 'text'
                        }
                        placeholder={c.label}
                        style={{ width: c.width || 90 }}
                        value={entry[c.k] ?? ''}
                        onChange={(e) => {
                          const v = e.target.value
                          setEntry((p) => ({ ...p, [c.k]: v }))
                        }}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  )
                })}
                {/* The HQ working columns are typeable on the entry row too —
                    PO is the exception, it is minted server-side. */}
                {EXTRA_COLS.map((c) => (
                  <TableCell key={c.k} className={classes.cell}>
                    {c.k === 'po_number' ? (
                      <span style={{ color: '#999' }}>auto</span>
                    ) : (
                      <TextField
                        className={classes.editInput}
                        type={c.k === 'expected_delivery' ? 'date' : 'text'}
                        placeholder={c.label}
                        style={{ width: c.k === 'expected_delivery' ? 130 : 110 }}
                        value={entry[c.k] ?? ''}
                        onChange={(e) => {
                          const v = e.target.value
                          setEntry((p) => ({ ...p, [c.k]: v }))
                        }}
                      />
                    )}
                  </TableCell>
                ))}
                    <TableCell className={classes.cell} align="right">
                      <Tooltip title="Remove this row" arrow>
                        <IconButton
                          size="small"
                          onClick={() => removeDraft(entry)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </Paper>

      {newCategoryDialog}

      {vendorMgr && (
        <VendorManager
          role={role}
          onClose={() => setVendorMgr(false)}
          onChanged={() => {}}
        />
      )}

      {/* Writing into the shared register is not undoable from here, so the
          dialog names the count and the tab before it happens, and says what
          it will skip. */}
      <Dialog
        open={pushOpen}
        onClose={() => setPushOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>Push to the Google daily sheet?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            <b>{pushSource().length}</b> line
            {pushSource().length === 1 ? '' : 's'}{' '}
            {selected.size ? 'ticked' : 'in view'} will be written to the tab
            for today, copied from the last date tab if it is not there yet.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Lines already on the sheet are skipped, so nothing is written twice.
            The monthly procurement report is made from this sheet.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setPushOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            color="primary"
            variant="contained"
            disableElevation
            disabled={pushBusy || !pushSource().length}
            onClick={sendToSheet}>
            {pushBusy ? 'Writing…' : 'Write to the sheet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!pushDone}
        onClose={() => setPushDone(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>On the sheet</DialogTitle>
        <DialogContent>
          {pushDone && (
            <>
              <Typography variant="body2" paragraph>
                <b>{pushDone.written}</b> line
                {pushDone.written === 1 ? '' : 's'} written to tab{' '}
                <b>{pushDone.tab}</b>
                {pushDone.created_tab ? ' (new tab)' : ''}
                {pushDone.skipped
                  ? ` · ${pushDone.skipped} already there, skipped`
                  : ''}
                .
              </Typography>
              {sheetTarget && sheetTarget.url && (
                <a href={sheetTarget.url} target="_blank" rel="noreferrer noopener">
                  Open the sheet
                </a>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setPushDone(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <TypedDeleteDialog
        open={confirmDel}
        count={selCount}
        onClose={() => setConfirmDel(false)}
        onConfirm={deleteSelected}
      />

      {/* Both stage moves are one-way, so each one asks first and names what
          it is about to move — the apartment and the line count, not a bare
          "are you sure?". */}
      <Dialog
        open={!!confirmAct}
        onClose={() => setConfirmAct(null)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>
          {confirmAct ? `${confirmAct.verb}?` : ''}
        </DialogTitle>
        <DialogContent>
          {confirmAct && (
            <>
              <Typography variant="body2" paragraph>
                <b>{confirmAct.lines}</b> line
                {confirmAct.lines === 1 ? '' : 's'} for{' '}
                <b>{confirmAct.apartment}</b>.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {confirmAct.note}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAct(null)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disableElevation
            disabled={busy}
            onClick={runConfirmed}>
            {confirmAct ? confirmAct.verb : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <PendingOrdersDialog
        kind={pendingKind}
        requests={pendingKind === 'review' ? toReview : toHandOver}
        liveLines={liveLines}
        busy={busy}
        onAct={askPending}
        onRemoveLine={pendingKind === 'review' ? removePendingLine : undefined}
        onClose={() => setPendingKind(null)}
      />

      <RequestTracking request={trackReq} onClose={() => setTrackReq(null)} />

      <Popover
        open={!!approvalReq}
        anchorEl={approvalReq && approvalReq.anchorEl}
        onClose={() => setApprovalReq(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <Box style={{ padding: 16, width: 280, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography variant="subtitle2">
            Approval sign-off
            {approvalReq && approvalReq.bulk
              ? ` · ${approvalReq.count} request${approvalReq.count === 1 ? '' : 's'}`
              : ''}
          </Typography>
          <Box>
            <Typography variant="caption" color="textSecondary">Needs approval?</Typography>
            <Select
              value={approvalForm.approval_needed || ''}
              displayEmpty
              fullWidth
              style={{ fontSize: 13 }}
              onChange={(e) => {
                const v = e.target.value
                setApprovalForm((p) => ({ ...p, approval_needed: v }))
              }}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </Box>
          <TextField
            size="small"
            label="Approved by"
            placeholder="e.g. Sonak"
            value={approvalForm.approved_by_name || ''}
            onChange={(e) => {
              const v = e.target.value
              setApprovalForm((p) => ({ ...p, approved_by_name: v }))
            }}
          />
          <Box>
            <Typography variant="caption" color="textSecondary">Source</Typography>
            <Select
              value={approvalForm.approval_source || ''}
              displayEmpty
              fullWidth
              style={{ fontSize: 13 }}
              onChange={(e) => {
                const v = e.target.value
                setApprovalForm((p) => ({ ...p, approval_source: v }))
              }}>
              <MenuItem value="">—</MenuItem>
              {APPROVAL_SOURCE_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </Select>
          </Box>
          <TextField
            size="small"
            label="Reference"
            placeholder="Subject or issue-tracker id"
            value={approvalForm.approval_reference || ''}
            onChange={(e) => {
              const v = e.target.value
              setApprovalForm((p) => ({ ...p, approval_reference: v }))
            }}
          />
          <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button size="small" onClick={() => setApprovalReq(null)}>Cancel</Button>
            <Button size="small" color="primary" variant="contained" disableElevation
              disabled={busy} onClick={submitApproval}>
              Save
            </Button>
          </Box>
        </Box>
      </Popover>

      {importOpen && (
        <DailyImportDialog
          onClose={() => setImportOpen(false)}
          onImported={load}
        />
      )}
    </Box>
  )
}

export default HqDailySheet
